// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { setIsLoaded } from '../../features/loaders/slice';
import { useReactTable, getCoreRowModel, flexRender, getGroupedRowModel, getExpandedRowModel, getFilteredRowModel } from '@tanstack/react-table';
import ContainerWithCard from '../../components/ContainerWithCard';
import { CrudToggles } from './CrudToggles';
import "./default.css";
import { PermissionMatrix } from '../../features/permissions/PermissionMatrix';
import PermissionProceduresTooltip from './PermissionProceduresTooltip';
import { getPermissionMatrixObjects, matchTextInPermissionObject } from '../../features/permissions/utilities';
import { getAllPermissions, updatePermissions } from '../../redux/methods/permissions';
import useFetch from '../../hooks/useFetch';
import { Button, Row } from 'reactstrap';
import { Link, generatePath } from 'react-router-dom';
import { getT, toPlainNested } from '../../utils';
import { useIntl } from 'react-intl';
import { setRolesExpanded } from '../../features/persisted/permissionsUi/slice';

// /**
//  * @param {LocalizedValue[]} value
//  */
// function localizedText(value)
// {
//     if (Array.isArray(value)) { return value[0]?.value ?? ''; }
//     // if (value && typeof value === 'object' && 'value' in value) return value.value ?? '';
//     return '???';
// }

/**
 * @param {IPermissionMatrix} permissionMatrix
 * @param {*} onToggle
 * @param {*} onFilterIconClick
 */
function getColumns(permissionMatrix, onToggle, onFilterIconClick)
{
    const objectColumn =
    {
        id: 'object',
        header: () =>
        {
            const element =
            <span className="d-flex justify-content-between">
                <button
                    type="button"
                    className="btn btn-sm btn-link p-0"
                    style={{ transform: 'rotate(180deg)' }}
                    aria-label="Filter"
                    title="Filtruj"
                    onClick={onFilterIconClick}
                >
                    <i className="fa fa-search" />
                </button>
                <span>Obiekt</span>
            </span>;
            return element;
        },
        // accessorKey: 'name',
        accessorFn: (/** @type {PermissionObjectEntity} */ row) => row?.name ?? '',
        enableSorting: true,
        sortingFn: 'alphanumeric',
        /** @type {import('@tanstack/react-table').ColumnDefTemplate<import('@tanstack/react-table').CellContext<PermissionObjectEntity, any>>} */
        cell: ({ row, getValue }) =>
        {
            if (row.getIsGrouped())
            {
                const objectGroupId = row.getValue('objectGroup');
                const label = permissionMatrix.permissionObjectsGroups.get(objectGroupId)?.name ?? '???';
                return (
                    <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={row.getToggleExpandedHandler()}
                        aria-label="toggle"
                    >
                        {row.getIsExpanded() ? '▾' : '▸'} {String(label)}
                    </button>
                );
            }
            else
            {
                return <span>{getValue()}</span>;
            }
        }
    };
    const operationsColumn =
    {
        id: 'operations',
        header: '',
        accessorKey: '',
        /** @type {import('@tanstack/react-table').ColumnDefTemplate<import('@tanstack/react-table').CellContext<PermissionObjectEntity, any>>} */
        cell: ({ row }) =>
        {
            if (row.getIsGrouped()) { return null; }
            const permissionObjectId = row.original.id;
            const elements = [];
            for (const permissionKind of [0, 1, 2, 3])
            {
                if (permissionMatrix.hasPermissionKind(permissionObjectId, permissionKind))
                {
                    elements.push
                    (
                        <PermissionProceduresTooltip permissionObjectId={row.original.id} permissionKind={permissionKind} matrix={permissionMatrix} />
                    );
                }
            }
            return <div className='crud-cell'>{elements}</div>;
        }
    };
    const objectGroupColumn =
    {
        id: 'objectGroup',
        header: 'Grupa obiektów',
        accessorKey: 'permissionObjectGroupId',
    };
    const rolesColumns = permissionMatrix.roles.map(role => (
    {
        id: role.id,
        // header: () => <span>{localizedText(role.name)}</span>,
        header: () => (
            <Link
                to={generatePath('/roles/:roleId/edit', { roleId: role.id })}
                className="text-decoration-none"
            >
                {role?.name}
            </Link>
        ),
        /** @type {import('@tanstack/react-table').ColumnDefTemplate<import('@tanstack/react-table').CellContext<PermissionObjectEntity, any>>} */
        cell: ({ row }) => row.getIsGrouped()
            ? null
            : <CrudToggles roleId={role.id} permissionObjectId={row.original.id} matrix={permissionMatrix} onToggle={onToggle} />,
    }));
    return [objectColumn, operationsColumn, objectGroupColumn, ...rolesColumns];
}

/**
 * @typedef {object} RolesViewProperties
 * @property {() => void} setIsLoaded
 * @property {() => Promise<{ pages: any; payload: any; } | undefined>} getAllPermissions
 * @property {(modifications: any) => Promise<{ pages: any; payload: any; } | undefined>} updatePermissions
 * @property {{[key: string]: boolean}} [rolesExpanded]
 * @property {(expanded: any) => void} [setRolesExpanded]
 * @property {boolean} [forceRefetch]
 */

/**
 * @param {RolesViewProperties} param0
 */
function RolesView({ getAllPermissions, updatePermissions, rolesExpanded, setRolesExpanded, forceRefetch })
{
    const intl = useIntl();
    const t = getT(intl.messages.permissions);
    const [filterText, setFilterText] = useState('');
    const [filterOpen, setFilterOpen] = useState(false);
    // eslint-disable-next-line no-undefined
    const [entities] = useFetch(getAllPermissions, null, [], (forceRefetch === undefined ? true : forceRefetch));
    // eslint-disable-next-line no-undefined
    const permissionMatrix = useMemo(() => (entities ? new PermissionMatrix(entities) : undefined), [entities]);

    /** @type {(role: number, object: number, permission: number, next: boolean) => void} */
    const onToggle = (role, object, permission, next) =>
    {
        if (!permissionMatrix) { return; }
        permissionMatrix.set(role, object, permission, next);
    };
    const onFilterIconClick = useCallback
    (
        () =>
        {
            // const rect = e.currentTarget.getBoundingClientRect();
            // setFilterPos({ top: rect.bottom + window.scrollY + 6, left: rect.left + window.scrollX });
            setFilterOpen(prev => !prev);
        },
        []
    );
    const globalFilterFn = useCallback
    (
        (row, _columnId, value) => matchTextInPermissionObject(permissionMatrix, row, _columnId, value),
        [permissionMatrix]
    );
    const data = useMemo(() => getPermissionMatrixObjects(permissionMatrix), [permissionMatrix]);
    const columns = useMemo(() => permissionMatrix ? getColumns(permissionMatrix, onToggle, onFilterIconClick) : [], [permissionMatrix, onFilterIconClick]);
    const grouping = useMemo(() => ['objectGroup'], []);
    const columnVisibility = useMemo(() => ({ objectGroup: false }), []);
    const expanded = rolesExpanded || {};
    const table = useReactTable(
    {
        data,
        columns,
        state:
        {
            grouping,
            columnVisibility,
            expanded,
            globalFilter: filterText
        },
        onGlobalFilterChange: setFilterText,
        globalFilterFn,
        autoResetExpanded: false,
        onExpandedChange: updater =>
        {
            const next = typeof updater === 'function' ? updater(expanded) : updater;
            setRolesExpanded(next);
        },
        getRowId: row => String(row.id),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getGroupedRowModel: getGroupedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        // getRowCanExpand: row => row.getIsGrouped(),
    });

    if (!permissionMatrix) { return null; }
    if (columns.length === 0) { return null; }
    const onSubmit = async () =>
    {
        await updatePermissions(toPlainNested(permissionMatrix.modifications));
    };
    const element =
    <ContainerWithCard title={'Role'} iconClass='fa fa-masks-theater'>
        {
            filterOpen &&
            <div className="roles-filter-popover mx-4 my-2">
                <div className="d-flex align-items-center">
                    <i className="fa fa-search mr-2" />
                    <input
                        autoFocus
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t("Filtruj po nazwie lub operacjach…")}
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Escape') { setFilterOpen(false); } }}
                        style={{ width: 280 }}
                    />
                    <button type="button" className="btn btn-sm btn-link ml-2" onClick={() => setFilterText('')} title="Wyczyść">
                        <i className="fa fa-times" />
                    </button>
                    <button type="button" className="btn btn-sm btn-link" onClick={() => setFilterOpen(false)} title="Zamknij">
                        <i className="fa fa-chevron-up" />
                    </button>
                </div>
            </div>
        }
        <div className="permissions-wrap mx-4 my-2">
            <table className="table table-sm m-0 permissions-table">
                <thead>
                {
                    table.getHeaderGroups().map
                    (
                        group =>
                        <tr key={group.id}>
                        {
                            group.headers.map
                            (
                                header => <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
                            )
                        }
                        </tr>
                    )
                }
                </thead>
                <tbody>
                {
                    table.getRowModel().rows.map
                    (
                        row =>
                        <tr key={row.id} className={row.getIsGrouped() ? 'group-row' : ''}>
                        {
                            row.getVisibleCells().map
                            (
                                cell => <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                            )
                        }
                        </tr>
                    )
                }
                </tbody>
            </table>
        </div>
        <Row className={"d-flex flex-row justify-content-between mx-4 my-2 flex-row"}>
            <Button color="primary" tag={Link} to={generatePath('/roles/:roleId/edit', { roleId: 0 })}>
                <i className="fa fa-plus mr-2"/>
                <span>{t('Create Role')}</span>
            </Button>
            <Button type={'submit'} onClick={onSubmit} color="success">
                <i className={"fa fa-save mr-2"}/>
                {
                    <span>{t('Save Changes')}</span>
                }
            </Button>
        </Row>
    </ContainerWithCard>;
    return element;
}
const mapDispatch =
{
    setIsLoaded,
    getAllPermissions,
    updatePermissions,
    setRolesExpanded,
};
const mapStateToProps = (/** @type {any} */ state) => ({
    user: state.auth.user,
    rolesExpanded: state.persisted?.permissionsUi?.rolesExpanded,
    forceRefetch: state.refetchers?.permissions,
});
export default connect(mapStateToProps, mapDispatch)(RolesView);